import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '../styles/leaflet-dark-theme.css';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as ThemeMode) || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    // Set data-theme attribute on document element for CSS variables
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo<Theme>(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'dark' ? '#64b5f6' : '#1d388d', // Lighter blue for dark mode, company color for light
            light: mode === 'dark' ? '#90caf9' : '#4a5fb8',
            dark: mode === 'dark' ? '#42a5f5' : '#142964',
            contrastText: '#ffffff',
          },
          secondary: {
            main: mode === 'dark' ? '#5a7ec7' : '#f50057', // Blue variation for dark mode
            light: mode === 'dark' ? '#7a9ad7' : '#ff4081',
            dark: mode === 'dark' ? '#3a5ea7' : '#c51162',
          },
          ...(mode === 'light'
            ? {
                // Light mode
                background: {
                  default: 'rgba(245, 245, 245, 0.85)', // Semi-transparent
                  paper: '#ffffff',
                },
                text: {
                  primary: '#000000',
                  secondary: '#666666',
                },
              }
            : {
                // Dark mode with blue variations
                background: {
                  default: 'rgba(10, 25, 41, 0.85)', // Semi-transparent dark blue background
                  paper: '#132f4c', // Darker blue for cards/papers
                },
                text: {
                  primary: '#ffffff',
                  secondary: '#b0c4de', // Light steel blue for secondary text
                },
                divider: '#2d4a6f', // Blue-tinted divider
                action: {
                  active: '#66b2ff',
                  hover: 'rgba(102, 178, 255, 0.08)',
                  selected: 'rgba(102, 178, 255, 0.16)',
                  disabled: 'rgba(255, 255, 255, 0.3)',
                  disabledBackground: 'rgba(255, 255, 255, 0.12)',
                },
              }),
          success: {
            main: mode === 'dark' ? '#66bb6a' : '#4caf50',
          },
          warning: {
            main: mode === 'dark' ? '#ffa726' : '#ff9800',
          },
          error: {
            main: mode === 'dark' ? '#f44336' : '#d32f2f',
          },
          info: {
            main: mode === 'dark' ? '#29b6f6' : '#0288d1',
          },
        },
        typography: {
          fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: 'transparent',
                backgroundImage: 'none',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: 8,
              },
              outlined: mode === 'dark' ? {
                color: '#e3f2fd', // Much brighter text for outlined buttons in dark mode
                borderColor: 'rgba(227, 242, 253, 0.5)',
                '&:hover': {
                  borderColor: '#e3f2fd',
                  backgroundColor: 'rgba(227, 242, 253, 0.08)',
                },
              } : {},
              text: mode === 'dark' ? {
                color: '#e3f2fd', // Brighter text for text buttons in dark mode
                '&:hover': {
                  backgroundColor: 'rgba(227, 242, 253, 0.08)',
                },
              } : {},
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: mode === 'dark' ? {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#90caf9', // Light blue for focused state
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#64b5f6', // Medium light blue for hover
                },
              } : {},
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: mode === 'dark' ? {
                '&.Mui-focused': {
                  color: '#90caf9', // Light blue for focused label
                },
              } : {},
            },
          },
          MuiSelect: {
            styleOverrides: {
              select: mode === 'dark' ? {
                '&:focus': {
                  backgroundColor: 'transparent',
                },
              } : {},
              icon: mode === 'dark' ? {
                color: '#90caf9', // Light blue for dropdown icon
              } : {},
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: mode === 'dark' ? {
                '&.Mui-selected': {
                  backgroundColor: 'rgba(144, 202, 249, 0.16)', // Light blue selected background
                  '&:hover': {
                    backgroundColor: 'rgba(144, 202, 249, 0.24)', // Slightly darker on hover
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(144, 202, 249, 0.08)',
                },
              } : {},
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                ...(mode === 'dark' && {
                  backgroundImage: 'none', // Remove default Material-UI dark mode gradient
                }),
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                ...(mode === 'dark' && {
                  backgroundColor: '#132f4c', // Consistent dark blue for cards
                  backgroundImage: 'none',
                }),
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: '#1d388d', // Always blue, regardless of theme
                color: '#ffffff',
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                ...(mode === 'dark' && {
                  borderColor: '#2d4a6f', // Blue-tinted borders for dark mode
                }),
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

